import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/admin/ResourceTable";
import { Gift, Package, Plus, Loader2 } from "lucide-react";

const FLOW = ["awaiting", "confirmed", "processing", "dispatched", "out_for_delivery", "delivered", "failed", "cancelled"];
const tone = (s) => ({ delivered: "green", confirmed: "blue", dispatched: "blue", out_for_delivery: "blue", processing: "amber", awaiting: "gray", failed: "red", cancelled: "red" }[s] || "gray");

const CATEGORIES = ["🏠 Home & Kitchen", "🧰 Tools & Equipment", "👕 Merchandise", "📱 Electronics"];
const EMPTY = { name: "", points_cost: 500, inventory: 10, image: "", description: "", category: CATEGORIES[0] };

export default function GiftsAdmin() {
  const [tab, setTab] = useState("catalog");
  const [gifts, setGifts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => { api.get("/gifts").then((r) => setGifts(r.data)); api.get("/gift-orders").then((r) => setOrders(r.data.items || [])); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/gifts", form); toast.success("Gift added"); setForm(EMPTY); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setCategory = async (id, category) => { try { await api.patch(`/gifts/${id}`, { category }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const setStatus = async (id, status) => {
    const extra = {};
    if (status === "dispatched") { extra.courier = window.prompt("Courier name (optional)") || ""; extra.tracking = window.prompt("Tracking link (optional)") || ""; extra.expected_date = window.prompt("Expected delivery date (optional)") || ""; }
    if (status === "failed") { extra.reason = window.prompt("Reason for delay / failure (optional)") || ""; extra.expected_date = window.prompt("New expected date (optional)") || ""; }
    try { await api.patch(`/gift-orders/${id}/status`, { status, ...extra }); toast.success(`Order → ${status}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Gifts & Orders</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">Catalogue, inventory reservations & order lifecycle</p>
      <div className="flex gap-2 mb-5">
        {[["catalog", "Catalogue"], ["orders", "Orders"]].map(([k, l]) => (
          <button key={k} data-testid={`gifts-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "catalog" && (
        <>
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
            <h3 className="font-display font-bold text-gray-900 mb-3 flex items-center gap-2"><Plus className="h-4 w-4" /> Add Gift</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <input data-testid="gift-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
              <input data-testid="gift-points" type="number" placeholder="Points" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: +e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
              <input data-testid="gift-inventory" type="number" placeholder="Inventory" value={form.inventory} onChange={(e) => setForm({ ...form, inventory: +e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
              <input data-testid="gift-image" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000]" />
              <select data-testid="gift-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#8B0000] bg-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button data-testid="gift-add-btn" onClick={create} disabled={busy || !form.name} className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-4 py-2 rounded-lg disabled:opacity-50">Add</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gifts.map((g) => (
              <div key={g.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`gift-card-${g.id}`}>
                {g.image ? <img src={g.image} alt={g.name} className="h-36 w-full object-cover" /> : <div className="h-36 bg-gray-100 flex items-center justify-center"><Gift className="h-10 w-10 text-gray-300" /></div>}
                <div className="p-4">
                  <div className="flex items-center justify-between"><h4 className="font-bold text-gray-900">{g.name}</h4><Badge tone={g.active ? "green" : "gray"}>{g.active ? "active" : "off"}</Badge></div>
                  <p className="text-sm text-[#8B0000] font-bold mt-1">{g.points_cost} pts</p>
                  <p className="text-xs text-gray-500 mt-2">Stock: {g.inventory - (g.reserved || 0)} available · {g.reserved || 0} reserved</p>
                  <select data-testid={`gift-card-category-${g.id}`} value={g.category || ""} onChange={(e) => setCategory(g.id, e.target.value)} className="mt-3 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#8B0000] bg-white">
                    {!g.category && <option value="">No category (hidden on WhatsApp)</option>}
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead><tr className="bg-gray-50 border-b">{["Order", "Customer", "Mobile", "Last dealer", "Depot", "Gift", "Points", "Delivery address", "PIN", "Status", "Update"].map((h) => <th key={h} className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>{orders.map((o) => (
              <tr key={o.id} className="border-b border-gray-100" data-testid={`gift-order-${o.id}`}>
                <td className="py-3 px-4 font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</td>
                <td className="py-3 px-4 whitespace-nowrap" data-testid={`gift-order-customer-${o.id}`}>{o.member_name || "—"}</td>
                <td className="py-3 px-4 font-mono text-xs" data-testid={`gift-order-mobile-${o.id}`}>{o.mobile || "—"}</td>
                <td className="py-3 px-4 text-xs" data-testid={`gift-order-dealer-${o.id}`}>{o.dealer_name || "—"}</td>
                <td className="py-3 px-4 text-xs" data-testid={`gift-order-depot-${o.id}`}>{o.depot || "—"}</td>
                <td className="py-3 px-4 font-semibold text-gray-900">{o.gift_name}</td>
                <td className="py-3 px-4">{o.points_cost}</td>
                <td className="py-3 px-4 text-xs text-gray-600 max-w-xs whitespace-pre-line" data-testid={`gift-order-address-${o.id}`}>{o.address || "—"}</td>
                <td className="py-3 px-4">{o.pin}</td>
                <td className="py-3 px-4"><Badge tone={tone(o.status)}>{o.status}</Badge></td>
                <td className="py-3 px-4">
                  <select data-testid={`order-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#8B0000] bg-white">
                    {FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>))}
              {orders.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-gray-400">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
